/**
 * "Is there a post this visitor has not seen?" — the dot on Blog in the menu.
 *
 * One first-party cookie holds a date: everything published up to it counts
 * as seen. It starts as the day of the visitor's first visit (set by the
 * proxy, `firstVisitSeen`) and moves to a post's date when they open a newer
 * one. It only ever moves forward: reading an older post after a newer one
 * does not un-read the newer one. A newer listed post than that date is what
 * the menu marks.
 *
 * Starting at the first visit: a new visitor sees no mark (a dot for
 * "everything" is noise), and a returning one — even one who never opened a
 * post — is told what was published since.
 *
 * Dates, not slugs: a post's date is shared across translations and orders
 * the corpus. Two posts published on the same day count as one for this.
 *
 * Pure and dependency-free, so the post page's client component and the
 * server-rendered nav read the same rule. Listed in the privacy policy (§13).
 */

import { cookieFromHeader } from "../cookie-header";

export const BLOG_SEEN_COOKIE = "sq_blog_seen";

/** About a year: long enough that a returning reader still gets the dot. */
export const BLOG_SEEN_MAX_AGE_SECONDS = 365 * 24 * 60 * 60;

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** The stored date, or null when absent or not a date we wrote. */
export const parseSeen = (raw: string | undefined): string | null =>
  raw !== undefined && ISO_DATE.test(raw) ? raw : null;

/** The stored date from a raw `Cookie` header (`document.cookie` in the browser). */
export const seenFromCookieHeader = (cookieHeader: string): string | null =>
  parseSeen(cookieFromHeader(cookieHeader, BLOG_SEEN_COOKIE));

/** What a visitor without the cookie starts with: today (UTC, the calendar
 *  the post dates use), so nothing already published is "new" to them. */
export const firstVisitSeen = (now: Date): string => now.toISOString().slice(0, 10);

/** Has a post been published since this visitor's date? Never without one
 *  (the proxy sets it on the first page view, so only before that). */
export const hasUnreadPost = (seen: string | null, latestPostDate: string | null): boolean =>
  seen !== null && latestPostDate !== null && latestPostDate > seen;

/** What the cookie should hold after opening a post dated `postDate`: the
 *  later of the two, so the mark never comes back for a post already read. */
export const nextSeen = (seen: string | null, postDate: string): string =>
  seen !== null && seen >= postDate ? seen : postDate;
